import * as React from "react";
import StudentTag from "../../../../domains/StudentTag";
import { useFlashMessage } from "../../../../hooks/useFlashMessage";
import { OperatorRoleTypes } from "../../../../interfaces/OperatorInterface";
import { ActiveOperator } from "../../../../interfaces/SectionOperatorsInterface";
import DeleteButton from "./DeleteButton";
import OperatorFullName from "./OperatorFullName";
import OperatorTagForm from "./OperatorTagForm";
import RoleSelect from "./RoleSelect";
import styles from "./styles.scss";
import { useChangeOperatorRole } from "./useChangeOperatorRole";
import { useDeleteOperator } from "./useDeleteOperator";
import { useUpdateOperatorSectionTags } from "./useUpdateOperatorSectionTags";
import ZoomStatus from "./ZoomStatus";

interface Props {
  operator: ActiveOperator;
  sectionId: string;
  sectionName: string;
  currentOperatorId: string;
  sectionTags: StudentTag[];
}

const OperatorTableRow: React.FC<Props> = ({
  operator,
  sectionId,
  sectionName,
  currentOperatorId,
  sectionTags,
}) => {
  const changeOperatorRoleMutation = useChangeOperatorRole();
  const deleteOperatorMutation = useDeleteOperator();
  const updateTagsMutation = useUpdateOperatorSectionTags();

  const { showSuccessMessage, showErrorMessage } = useFlashMessage();

  const isPending =
    changeOperatorRoleMutation.isPending ||
    deleteOperatorMutation.isPending ||
    updateTagsMutation.isPending;

  const handleRoleChange = (value: OperatorRoleTypes) => {
    if (isPending) {
      return;
    }

    changeOperatorRoleMutation.mutate(
      {
        sectionId,
        operatorId: operator.id,
        role: value,
      },
      {
        onSuccess: () => {
          showSuccessMessage("アカウントの権限を変更しました");
        },
        onError: () => {
          showErrorMessage("アカウントの権限を変更できませんでした");
        },
      },
    );
  };

  const handleDelete = () => {
    if (isPending) {
      return;
    }

    if (
      window.confirm(
        `${operator.fullName}さんの${sectionName}へのスタッフ登録を削除してよろしいですか？`,
      )
    ) {
      deleteOperatorMutation.mutate(
        {
          sectionId,
          operatorId: operator.id,
        },
        {
          onSuccess: () => {
            showSuccessMessage("アカウントの権限を停止しました");
          },
          onError: () => {
            showErrorMessage("アカウントの権限を停止できませんでした");
          },
        },
      );
    }
  };

  const handleTagsChange = (tagIds: string[], onSuccess: () => void) => {
    if (isPending) {
      return;
    }

    updateTagsMutation.mutate(
      {
        sectionId,
        operatorId: operator.id,
        tagIds,
      },
      {
        onSuccess: () => {
          showSuccessMessage("アカウントの権限を変更しました");
          onSuccess(); // モーダルを閉じる用のコールバック
        },
        onError: () => {
          showErrorMessage("アカウントの権限を変更できませんでした");
        },
      },
    );
  };

  return (
    <tr className={styles.tableRow}>
      <td>
        <OperatorFullName fullName={operator.fullName} />
      </td>
      <td>{operator.email}</td>
      <td>
        <RoleSelect
          operatorId={operator.id}
          role={operator.role}
          isPending={isPending}
          currentOperatorId={currentOperatorId}
          onRoleChange={handleRoleChange}
        />
      </td>
      <td>
        <OperatorTagForm
          operator={operator}
          currentOperatorId={currentOperatorId}
          sectionId={sectionId}
          sectionTags={sectionTags}
          isPending={isPending}
          onTagsChange={handleTagsChange}
        />
      </td>
      <td>
        <ZoomStatus isConnected={operator.zoomConnected} />
      </td>
      <td>
        <DeleteButton
          operatorId={operator.id}
          isAdmin={operator.isAdmin}
          currentOperatorId={currentOperatorId}
          onDelete={handleDelete}
        />
      </td>
    </tr>
  );
};

export default OperatorTableRow;
